/**
 * Jargon with its meaning attached.
 *
 * The tool is read by people who are not technicians, and the protocol's
 * vocabulary leaks into the UI in places where no plainer word exists. "MIL" is
 * the clearest case: it is the check engine light, and nothing on screen said
 * so.
 *
 * Built on `<abbr>` rather than a custom tooltip. It carries the meaning to
 * screen readers, works on keyboard focus, survives a page print, and needs no
 * positioning logic that can end up rendering off-screen on a phone.
 */

const GLOSSARY: Record<string, string> = {
  MIL: 'Malfunction Indicator Lamp — the check engine light on the dashboard',
  DTC: 'Diagnostic Trouble Code — the fault code a module stores, like P0420',
  PID: 'Parameter ID — one live reading the vehicle can report, such as coolant temperature',
  VIN: 'Vehicle Identification Number — the 17-character serial number unique to this vehicle',
  ECU: 'Electronic Control Unit — one of the computers on the vehicle',
  ECM: 'Engine Control Module — the computer managing the engine',
  OBD: 'On-Board Diagnostics — the standard port and protocol fitted to every car since the mid-1990s',
  'freeze frame': 'A snapshot of the engine readings taken at the instant a fault was recorded',
  readiness: "Self-tests the vehicle runs in the background. All must finish before an emissions test can pass",
  'mode 06': 'On-board test results — the vehicle’s own measurements with the limits it judges them against',
  'pending code': 'A fault seen once but not yet confirmed on a second drive. It becomes a stored code if it repeats',
  'permanent code': 'A fault the vehicle will not let you clear. It clears itself once the repair is verified',
  'fuel trim': 'How far the engine is adjusting fuel away from its baseline to keep the mixture correct',
  'drive cycle': 'A pattern of driving that lets the vehicle finish its self-tests, usually a mix of town and highway',
  stored: 'A confirmed fault. The vehicle has seen it on more than one drive and turned the warning light on',
  pending: 'Seen once but not yet confirmed. It becomes a stored code if it happens again',
  permanent: 'Set by the vehicle and not clearable by a tool. It clears itself once the repair is verified',
  clean: 'No fault codes stored, pending or permanent at the time of this scan',
  'no mileage': 'No odometer reading recorded, so distance-based service intervals cannot be judged',
  parts: 'The last scan has codes with parts listed against them',
  OBDMID: 'Monitor ID — which on-board self-test a mode 06 result belongs to',
  TID: 'Test ID — which measurement within a monitor. Values above 0x80 are manufacturer-defined',
  UAS: 'Unit and Scaling ID — tells the tool what units a mode 06 value is in and how to scale it',
  'oil-life monitor': 'The vehicle decides when an oil change is due from how it has been driven, rather than a fixed mileage',
};

export function Term({ children, of }: { children: React.ReactNode; of?: string }) {
  const key = of ?? (typeof children === 'string' ? children : '');
  const meaning = GLOSSARY[key] ?? GLOSSARY[key.toLowerCase()];
  if (!meaning) return <>{children}</>;

  return (
    <abbr className="term" title={meaning}>
      {children}
    </abbr>
  );
}

export const glossaryTerms = GLOSSARY;
